import type { PlanTableSorting, TableData, TableRow } from './types'

// Port of `sort_table_data` in `server/src/md_core/export/models/plan_table_sorting.py`, so the
// Planansicht lists the rows in the order the exported plan does. Change both together.

const CHUNK_PATTERN = /([0-9]+)/
const NUMBER_PATTERN = /^[0-9]+$/

const compare = <T extends string | number | bigint | boolean>(a: T, b: T): number =>
  a > b ? 1 : a < b ? -1 : 0

/** The text split into digit runs and the text between them. */
const sortKey = (text: string): string[] =>
  text
    .trim()
    .toLowerCase()
    .split(CHUNK_PATTERN)
    .filter(chunk => chunk !== '')

const cellSortKey = (row: TableRow, columnKey: string): string[] => {
  const cell = row.cells.find(item => item.columnKey === columnKey)
  return cell ? sortKey(cell.value.join('\n')) : []
}

const compareSortKeys = (keyA: string[], keyB: string[]): number => {
  for (let index = 0; index < Math.min(keyA.length, keyB.length); index++) {
    const chunkA = keyA[index]
    const chunkB = keyB[index]
    const isNumberA = NUMBER_PATTERN.test(chunkA)
    const isNumberB = NUMBER_PATTERN.test(chunkB)
    let result: number
    if (isNumberA && isNumberB) {
      result = compare(BigInt(chunkA), BigInt(chunkB))
    } else if (isNumberA !== isNumberB) {
      result = isNumberA ? -1 : 1
    } else {
      result = compare(chunkA, chunkB)
    }
    if (result !== 0) return result
  }
  return compare(keyA.length, keyB.length)
}

/** Natural order: digit runs compare by value ("Pos 2" before "Pos 10"). */
export const compareSortTexts = (a: string, b: string): number =>
  compareSortKeys(sortKey(a), sortKey(b))

/** Sorts the body rows; empty cells stay last and ties keep their order. */
export const sortTableData = (
  table: TableData,
  sorting: PlanTableSorting | null | undefined,
): TableData => {
  if (!sorting || !table.columns.some(column => column.key === sorting.columnKey)) return table
  const sign = sorting.direction === 'desc' ? -1 : 1
  // One key per row, not per comparison.
  const keys = table.rows.map(row => cellSortKey(row, sorting.columnKey))
  const order = table.rows
    .map((_, index) => index)
    .sort((indexA, indexB) => {
      const keyA = keys[indexA]
      const keyB = keys[indexB]
      if (keyA.length === 0 || keyB.length === 0)
        return compare(keyA.length === 0, keyB.length === 0)
      return sign * compareSortKeys(keyA, keyB)
    })
  // Already in order: keep the table, so memoized consumers don't recompute.
  if (order.every((rowIndex, index) => rowIndex === index)) return table
  return { ...table, rows: order.map(index => table.rows[index]) }
}

// The last sorted result per table content. Store and query data keep their identity while
// unchanged, so repeated reads (every render, every table in the scene) reuse one sorted table.
const sortedTables = new WeakMap<TableData, { sorting: PlanTableSorting; sorted: TableData }>()

/** `sortTableData`, reusing the previous result while the table and its sorting are unchanged. */
export const getSortedTableData = (
  table: TableData,
  sorting: PlanTableSorting | null | undefined,
): TableData => {
  if (!sorting) return table
  const cached = sortedTables.get(table)
  if (
    cached &&
    cached.sorting.columnKey === sorting.columnKey &&
    cached.sorting.direction === sorting.direction
  )
    return cached.sorted
  const sorted = sortTableData(table, sorting)
  sortedTables.set(table, { sorting, sorted })
  return sorted
}
